import styles from "./CartDetails.module.css";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import Spin from "../../Spinner/Spinner";
import { toast } from "react-toastify";
import { DeleteConfirmation } from "./DeleteConfirmation";
import { CancelOrderConfirmation } from "./CancelOrderConfirmation";
import { ProductImages } from "./ProductImages";
import { FaPen } from "react-icons/fa";
import { API } from "../../../config";
import { useCartDetails } from "./useCartDetails";
import { AiOutlinePlus } from "react-icons/ai";
import { CreateProductDialog } from "./CreateProductDialog";
import { UploadingDialog } from "../../Catalogs/Dialog/UploadingDialog";
import { useNavigate } from "react-router-dom";
import { TextEditor } from "../../Blogs/TextEditor";
import { OrderImagesDialog } from "./OrderImagesDialog";
import { TfiReload } from "react-icons/tfi";

const statusColors = {
  fulfilled: "#03ce00",
  cancelled: "#e12222",
  processing: "#ffa500",
  "active-cart": "#6687fa",
};
export const CartDetails = () => {
  const {
    cartId,
    loading,
    cartDetails,
    products,
    totalAmountWithTax,
    totalAmountWithoutTax,
    totalTax,
    totalAmountWithoutDiscount,
    cartNotes,
    setCartNotes,
    cartName,
    setCartName,
    isEditingName,
    setIsEditingName,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    showProductImages,
    setShowProductImages,
    productImages,
    mockupImages,
    defaultImage,
    getCartDetails,
    handleChangeImages,
    updateCartProduct,
    deleteProductFromCart,
    handleCancelDeleteConfirmation,
    handleConfirmDeleteConfirmation,
    handleAddDiscount,
    handleDownloadPdf,
    handleUpdateCartName,
    handleOrdersClick,
    showCancelOrderConfirmation,
    showMarkOrderConfirmation,
    handleCancelOrderCancel,
    handleCancelOrderConfirm,
    handleMarkOrderCancel,
    handleMarkOrderConfirm,
  } = useCartDetails();

  const [showOrderImagesDialog, setShowOrderImagesDialog] = useState(false);

  const handleShareLink = () => {
    const shareLink = `https://hustlemad.com/carts/${cartId}`;
    navigator.clipboard.writeText(shareLink);
    toast.success("Share link copied to clipboard");
  };

  if (loading) {
    return <Spin />;
  }
  return (
    <div className={styles.container}>
      <div className={styles.flexContainer}>
        <div className={styles.productsContainer}>
          <div className={styles.productsHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {isEditingName ? (
                <>
                  <input
                    type="text"
                    value={cartName}
                    onChange={(e) => setCartName(e.target.value)}
                    className={styles.cartNameInput}
                    placeholder="Cart name"
                  />
                  <button
                    type="button"
                    className={styles.cartNameSaveButton}
                    onClick={handleUpdateCartName}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className={styles.cartNameCancelButton}
                    onClick={() => {
                      setCartName(cartDetails?.cart?.name || "");
                      setIsEditingName(false);
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <h1>{cartDetails?.cart?.name || "Untitled Cart"}</h1>
                  <button
                    type="button"
                    className={styles.cartNameEditButton}
                    onClick={() => setIsEditingName(true)}
                    aria-label="Edit cart name"
                  >
                    <FaPen />
                  </button>
                </>
              )}
            </div>
            <button
              className={styles.deleteBttn}
              onClick={() => setShowDeleteConfirmation(true)}
            >
              Delete Cart
            </button>
          </div>
          <div
            style={{
              backgroundColor: statusColors[cartDetails?.cart?.status],
              width: "fit-content",
              padding: "5px 10px",
              borderRadius: "10px",
              color: "#fff",
              fontSize: "18px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            {cartDetails?.cart?.status}
          </div>
          <div className={styles.productsList}>
            {products.map((product) => (
              <ProductCard
                key={product.id}
                {...product}
                handleViewImages={() => {
                  setShowProductImages({ open: true, productId: product.id });
                }}
                handleDelete={() => {
                  deleteProductFromCart(product.id);
                }}
                handleSave={(data) => {
                  updateCartProduct(product.id, data);
                }}
              />
            ))}
          </div>
          <AddProductsToCart
            cartId={cartId}
            onProductsAdded={getCartDetails}
            alreadyAddedProducts={products.map((product) => product.id)}
            getCartDetails={getCartDetails}
          />
        </div>
        <div className={styles.summaryRightContainer}>
          <div className={styles.summaryHeaderOne}>
            <button
              className={styles.downloadPdfBttn}
              type="button"
              onClick={handleDownloadPdf}
            >
              Download PDF
            </button>
            <button
              className={styles.downloadPdfBttn}
              type="button"
              onClick={handleShareLink}
            >
              Share Link
            </button>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <SummarySection
              totalAmountWithTax={totalAmountWithTax}
              discount={cartDetails?.cart?.discount ?? 0}
              totalTaxableAmount={totalAmountWithoutTax}
              totalTax={totalTax}
              handleAddDiscount={handleAddDiscount}
              totalAmountWithoutDiscount={totalAmountWithoutDiscount}
            />
            <div
              className={styles.summaryContainer}
              style={{ paddingRight: "30px" }}
            ></div>
            <div
              className={styles.summaryContainer}
              style={{
                paddingRight: "30px",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div className={styles.summaryItem}>Notes</div>
              <div style={{ width: "100%" }}>
                <TextEditor
                  value={cartNotes || ""}
                  onChange={setCartNotes}
                  props={{
                    containerProps: {
                      style: {
                        minHeight: "150px",
                        maxHeight: "200px",
                        borderRadius: "10px",
                        backgroundColor: "white",
                      },
                    },
                  }}
                />
              </div>
            </div>
            <div
              className={styles.summaryContainer}
              style={{ paddingRight: "30px" }}
            >
              <div className={styles.summaryItem}>Cart Created On</div>
              <div className={styles.summaryValue} style={{ fontSize: 12 }}>
                {new Date(cartDetails?.cart?.createdAt).toLocaleDateString()}
              </div>
            </div>
            <div className={styles.bottomButtonContainer}>
              {cartDetails?.cart?.status === "active-cart" && (
                <button
                  onClick={() => handleOrdersClick("confirm")}
                  className={styles.markOrderBttn}
                >
                  Mark Order
                </button>
              )}
              {cartDetails?.cart?.status === "active-cart" && (
                <button
                  onClick={() => handleOrdersClick("cancel")}
                  className={styles.cancelOrderBttn}
                  style={{ flex: 1 }}
                >
                  Cancel Order
                </button>
              )}
            </div>
            <button
              className={styles.cartDiscountBttn}
              style={{ width: "100%" }}
              type="button"
              onClick={() => setShowOrderImagesDialog(true)}
            >
              Order Images
            </button>
          </div>
        </div>
      </div>
      <DeleteConfirmation
        show={showDeleteConfirmation}
        handleCancel={handleCancelDeleteConfirmation}
        handleConfirm={handleConfirmDeleteConfirmation}
      />
      <CancelOrderConfirmation
        show={showCancelOrderConfirmation}
        handleCancel={handleCancelOrderCancel}
        handleConfirm={handleCancelOrderConfirm}
        title="Cancel Order"
        message="This action will cancel the order for this cart. You can't undo this."
        cancelLabel="Keep Order"
        confirmLabel="Yes, Cancel"
      />
      <CancelOrderConfirmation
        show={showMarkOrderConfirmation}
        handleCancel={handleMarkOrderCancel}
        handleConfirm={handleMarkOrderConfirm}
        title="Mark Order"
        message="This will mark the cart as processing and move it to Orders."
        cancelLabel="Back"
        confirmLabel="Yes, Mark Order"
      />
      <OrderImagesDialog
        show={showOrderImagesDialog}
        onClose={() => setShowOrderImagesDialog(false)}
        cartId={cartId}
        orderImages={cartDetails?.cart?.orderImages || []}
        onImagesUpdated={getCartDetails}
        cartStatus={cartDetails?.cart?.status}
      />
      <ProductImages
        productImages={productImages}
        mockupImages={mockupImages}
        defaultImage={defaultImage}
        open={showProductImages.open}
        handleClose={() =>
          setShowProductImages({ open: false, productId: null })
        }
        handleUploadNewImage={(type, images) => {
          handleChangeImages(type, images, showProductImages.productId);
        }}
        handleChangeDefaultImage={(image) => {
          updateCartProduct(showProductImages.productId, {
            defaultImage: image,
          });
        }}
      />
    </div>
  );
};

const AddProductsToCart = ({
  cartId,
  onProductsAdded,
  alreadyAddedProducts = [],
  getCartDetails = () => {},
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [productsToSelectFrom, setProductsToSelectFrom] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});
  const debounceTimerRef = useRef(null);
  const [showCreateProductDialog, setShowCreateProductDialog] = useState(false);
  const [showUploadingDialog, setShowUploadingDialog] = useState(false);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await axios.get(`${API}/Admin/addProduct`, {
        params: {
          search: debouncedSearch || undefined,
          limit: 10,
          page: 1,
        },
      });
      setProductsToSelectFrom(response.data.products || []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleQuantityChange = (productId, quantity) => {
    const numQuantity = Number(quantity);
    if (numQuantity < 1) return;

    setSelectedProducts((prev) => ({
      ...prev,
      [productId]: numQuantity,
    }));
  };

  const handleAddProduct = (productId) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [productId]: prev[productId] || 1,
    }));
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prev) => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const handleSave = async () => {
    const productsToAdd = Object.entries(selectedProducts).map(
      ([productId, quantity]) => ({
        productId,
        quantity,
      }),
    );

    if (productsToAdd.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/Admin/carts/${cartId}/products`,
        {
          products: productsToAdd,
        },
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Products added to cart successfully");
        setSelectedProducts({});
        setSearch("");
        setDebouncedSearch("");
        setProductsToSelectFrom([]);
        if (onProductsAdded) {
          onProductsAdded();
        }
      } else {
        throw new Error("Failed to add products");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to add products to cart");
    }
  };

  const handleOpenCreateProductDialog = () => {
    setShowCreateProductDialog(true);
  };

  const handleCloseCreateProductDialog = () => {
    setShowCreateProductDialog(false);
  };

  return (
    <div className={styles.addProductsContainer}>
      <div className={styles.addProductsHeader}>
        <h3 className={styles.addProductsTitle}>Add Products to Cart</h3>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
          }}
        >
          <button
            type="button"
            className={styles.addProductsHeaderButton}
            onClick={() =>
              navigate(`/generate-kit?userId=${cartId}&cartId=${cartId}`)
            }
          >
            <TfiReload /> Generate Kit
          </button>
          <button
            type="button"
            className={styles.addProductsHeaderButton}
            onClick={handleOpenCreateProductDialog}
          >
            <AiOutlinePlus /> Create New Product
          </button>
        </div>
      </div>
      <div className={styles.addProductsSearchContainer}>
        <input
          type="text"
          className={styles.addProductsSearchInput}
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loadingProducts && (
        <div className={styles.addProductsLoading}>
          <Spin />
        </div>
      )}

      {!loadingProducts && productsToSelectFrom.length > 0 && (
        <div className={styles.addProductsList}>
          {productsToSelectFrom.map((product) => {
            const isSelected = selectedProducts[product._id];
            const quantity = selectedProducts[product._id] || 1;

            return (
              <div key={product._id} className={styles.addProductsItem}>
                <div className={styles.addProductsItemImage}>
                  {product.image?.[0] ? (
                    <img
                      src={product.image[0]}
                      alt={product.name || "Product"}
                    />
                  ) : (
                    <div className={styles.addProductsImagePlaceholder} />
                  )}
                </div>
                <div className={styles.addProductsItemInfo}>
                  <h4 className={styles.addProductsItemName}>
                    {product.name || "-"}
                  </h4>
                  <p className={styles.addProductsItemBrand}>
                    {product.brandName || "-"}
                  </p>
                </div>
                <div className={styles.addProductsItemActions}>
                  {isSelected ? (
                    <>
                      <div className={styles.addProductsQuantityContainer}>
                        <label className={styles.addProductsQuantityLabel}>
                          Quantity
                        </label>
                        <input
                          type="number"
                          className={styles.addProductsQuantityInput}
                          value={quantity}
                          min="1"
                          onChange={(e) =>
                            handleQuantityChange(product._id, e.target.value)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className={styles.addProductsRemoveButton}
                        onClick={() => handleRemoveProduct(product._id)}
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={styles.addProductsAddButton}
                      onClick={() => handleAddProduct(product._id)}
                      disabled={alreadyAddedProducts.includes(product._id)}
                    >
                      Add
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loadingProducts &&
        debouncedSearch &&
        productsToSelectFrom.length === 0 && (
          <div className={styles.addProductsNoResults}>No products found</div>
        )}

      {Object.keys(selectedProducts).length > 0 && (
        <div className={styles.addProductsSaveContainer}>
          <button
            type="button"
            className={styles.addProductsSaveButton}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      )}

      {showCreateProductDialog && (
        <CreateProductDialog
          show={showCreateProductDialog}
          onClose={handleCloseCreateProductDialog}
          onManual={() => navigate(`/products/add/${cartId}`)}
          onAddLink={() => toast.success("Product link captured")}
          onSuccess={() => getCartDetails()}
          onStartUpload={() => setShowUploadingDialog(true)}
          onEndUpload={() => setShowUploadingDialog(false)}
        />
      )}
      <UploadingDialog
        show={showUploadingDialog}
        onContinueBackground={() => setShowUploadingDialog(false)}
        estimatedMinutes={1}
      />
    </div>
  );
};

const ProductCard = ({
  name = "-",
  brand = "-",
  description = "-",
  itemDetails = [],
  image = "",

  price = 0,
  margin = 0,
  discount = 0,
  quantity = 0,
  gst = 0,

  customisation = "",
  remarks = "",

  handleViewImages = () => {},
  handleDelete = () => {},
  handleSave = () => {},
}) => {
  const [customisationValue, setCustomisationValue] = useState(customisation);
  const [remarksValue, setRemarksValue] = useState(remarks);
  const [units, setUnits] = useState(quantity);
  const [rate, setRate] = useState(price);
  const [gstValue, setGstValue] = useState(gst);

  useEffect(() => {
    setCustomisationValue(customisation);
    setRemarksValue(remarks);
    setUnits(quantity);
    setRate(price);
    setGstValue(gst);
  }, [customisation, remarks, quantity, gst, price]);

  const customisationOptions = [
    "Single Colour Screen Print",
    "Multi-colour ScreenPrint",
    "DTF",
    "Engraving",
    "Embroidery",
    "UV Print",
    "Digital Print",
    "FurnacePrint",
    "Embossing",
    "UV Embossed & Shape Cut",
    "N/A",
  ];

  const amount = rate * (1 + gstValue / 100);
  const discountedAmount = amount - ((discount ?? 0) / 100) * amount;

  return (
    <div className={styles.productCard}>
      <img src={image} alt={name} className={styles.productImage} />
      <div className={styles.columnContainer}>
        <h2 className={styles.productDetails}>{name}</h2>
        <p className={styles.brand}>{brand}</p>
        <p className={styles.subtextHeading}>Description</p>
        <p className={styles.subtext}>{description}</p>
        <p className={styles.subtextHeading}>Item Details</p>
        <p className={styles.subtext}>
          {itemDetails.map((item, index) => (
            <div key={index} className={styles.subtext}>
              {index + 1}. {item}
            </div>
          ))}
        </p>
      </div>
      <div className={styles.columnContainer}>
        <select
          className={styles.customisationSelect}
          value={customisationValue}
          onChange={(e) => setCustomisationValue(e.target.value)}
        >
          <option value="">Customization</option>
          {customisationOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <textarea
          className={styles.remarksTextarea}
          value={remarksValue}
          onChange={(e) => setRemarksValue(e.target.value)}
          placeholder="Remarks"
        />
      </div>
      <div className={styles.columnContainer}>
        <div className={styles.buttonContainer}>
          <button className={styles.viewBttn} onClick={handleViewImages}>
            View Images
          </button>
          <button className={styles.deleteBttn} onClick={handleDelete}>
            Delete
          </button>
        </div>
        <div className={styles.pricingSection}>
          <div className={styles.pricingField}>
            <label className={styles.pricingLabel}>Units</label>
            <input
              type="number"
              className={styles.pricingInput}
              value={units}
              onChange={(e) => setUnits(Number(e.target.value))}
              min="1"
            />
          </div>
          <div className={styles.pricingField}>
            <label className={styles.pricingLabel}>Rate</label>
            <input
              type="number"
              className={styles.pricingInput}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
            />
          </div>
          <div className={`${styles.pricingField} ${styles.gstField}`}>
            <label className={styles.pricingLabel}>GST</label>
            <select
              className={styles.pricingInput}
              value={gstValue}
              onChange={(e) => setGstValue(Number(e.target.value))}
            >
              <option value={0}>0%</option>
              <option value={5}>5%</option>
              {/* <option value={12}>12%</option> */}
              <option value={18}>18%</option>
              <option value={28}>28%</option>
            </select>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <button
              className={styles.saveBttn}
              onClick={() =>
                handleSave({
                  customisation: customisationValue,
                  remarks: remarksValue,
                  units: units,
                  rate: rate,
                  gst: gstValue,
                })
              }
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummarySection = ({
  totalAmountWithTax = 0,
  discount = 0,
  totalTaxableAmount = 0,
  totalAmountWithoutTax = 0,
  totalTax = 0,
  totalAmountWithoutDiscount = 0,
  handleAddDiscount = () => {},
}) => {
  const [addDiscount, setAddDiscount] = useState({
    discount: discount,
    show: false,
  });
  const [discountValue, setDiscountValue] = useState(discount);

  const handleApplyDiscount = () => {
    setAddDiscount({ discount: discountValue, show: false });
    handleAddDiscount(discountValue);
  };

  const handleCloseDiscount = () => {
    setAddDiscount({ ...addDiscount, show: false });
    setDiscountValue(discount);
  };

  return (
    <div className={styles.summarySection}>
      <div className={styles.summaryHeader}>Summary</div>
      {discount > 0 && (
        <div className={styles.summaryContainer}>
          <div className={styles.summaryItem}>Subtotal</div>
          <div className={styles.summaryValue}>
            {totalAmountWithoutDiscount}
          </div>
        </div>
      )}
      {discount > 0 && (
        <div className={styles.summaryContainer}>
          <div
            className={styles.summaryItem}
            style={{
              color: "#10b981",
            }}
          >
            Discount ({discount}%)
          </div>
          <div
            className={styles.summaryValue}
            style={{
              color: "#10b981",
            }}
          >
            -{Math.round(totalAmountWithoutDiscount * (discount / 100))}
          </div>
        </div>
      )}
      <div className={styles.summaryContainer}>
        <div className={styles.summaryItem}>Total Taxable Amount</div>
        <div className={styles.summaryValue}>{totalTaxableAmount}</div>
      </div>
      <div className={styles.summaryContainer}>
        <div className={styles.summaryItem}>Total Tax</div>
        <div className={styles.summaryValue}>{totalTax}</div>
      </div>

      <div className={styles.divider}></div>

      <div className={styles.bottomContainer}>
        <h6>{totalAmountWithTax}/-</h6>
        <div className={styles.summaryItem}>Total Amount</div>
        <button
          onClick={() => setAddDiscount({ ...addDiscount, show: true })}
          className={styles.cartDiscountBttn}
        >
          Add Cart Discount
        </button>
      </div>
      {addDiscount.show && (
        <div className={styles.discountContainer}>
          <button
            className={styles.crossIcon}
            onClick={handleCloseDiscount}
            aria-label="Close discount"
            type="button"
          >
            <span className={styles.crossIconText}>×</span>
          </button>
          <div className={styles.discountField}>
            <label className={styles.discountLabel}>Discount</label>
            <div className={styles.discountInputWrapper}>
              <input
                type="number"
                className={styles.discountInput}
                value={discountValue}
                onChange={(e) => {
                  const value = Math.min(
                    100,
                    Math.max(0, Number(e.target.value) || 0),
                  );
                  setDiscountValue(value);
                }}
                placeholder="10%"
                min="0"
                max="100"
              />
              <span className={styles.discountPercentSymbol}>%</span>
            </div>
          </div>
          <button
            className={styles.discountApplyBtn}
            onClick={handleApplyDiscount}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};
